import { observer } from "mobx-react";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ProgressAPI, ResultsAPI } from "generated/types";
import { useAdaptivePractice } from "hooks/useAdaptivePractice";
import { useProduct } from "hooks/useProduct";
import { useProfile } from "hooks/useProfile";
import { useRandomFact } from "hooks/useRandomFact";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import { useWriting } from "hooks/useWriting";
import { ModuleType, type ProductModules } from "models/product/ProductDetails";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { ProductType } from "constants/hst-constants";
import { PRODUCT_PAGES_ROUTES, TEST_ENVIRONMENT_ROUTES } from "constants/routes";

import { Heading1, Heading3, Heading4 } from "styles/elements/Headings";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import goToTestImg from "assets/images/go-to-test-image-person-red.svg";
import { Notification } from "components/Notification/Notification";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProductProgress } from "hooks/useProductProgress";
import { useRedirect } from "hooks/useRedirect";
import { ParagraphLarge } from "styles/elements/Texts";
import {
  StyledGoToTestButton,
  StyledMainSection,
  StyledTestEnvCard,
} from "./StudentDashboardPage.styled";

import {
  HstProductsWithPlaceholderModules,
  getComingSoonModules,
} from "constants/placeholder-constants";
import { ComingSoonModuleCard } from "./components/ComingSoonModuleCard/ComingSoonModuleCard";
import { ModuleCard } from "./components/ModuleCard/ModuleCard";

export const StudentDashboardPage: React.FC = observer(() => {
  const { t } = useTranslation("homepage");
  const { canSetInitialProgressFromIntakeTestResults, showComingSoonModules } = useFeatureFlags();

  const randomFact = useRandomFact();
  const { userDetails } = useProfile();
  const selectedProductId = useSelectedProductId();
  const { productDetails } = useProduct();
  const { redirectToUrl } = useRedirect();
  const {
    lastPracticedAdaptiveTopicStatuses,
    fetchLastPracticedAdaptiveTopicStatuses,
    modulesWithIntakeTestScores,
    fetchModulesWithIntakeTestScores,
  } = useAdaptivePractice();
  const { lastPracticedWritingTopicStatuses, fetchLastPracticedWritingTopicStatuses } =
    useWriting();
  const { fetchProductGoalLevel, productGoalLevel } = useProductProgress();

  // We should only show intake test notification if user still hasn't practiced in the adaptive modules,
  // they have intake scores available for their current goal level and they have permissions to use
  // the init progress from intake test functionality
  const shouldDisplayIntakeTestNotification =
    canSetInitialProgressFromIntakeTestResults &&
    userDetails?.canInitProgressFromIntakeTest &&
    modulesWithIntakeTestScores &&
    modulesWithIntakeTestScores.length > 0 &&
    (!lastPracticedAdaptiveTopicStatuses || lastPracticedAdaptiveTopicStatuses.length === 0);

  // Obtain object with current goal level
  const currentGoalLevel = useMemo(() => {
    if (!productGoalLevel?.goalLevel || !productDetails) {
      return null;
    }

    return productDetails.levels.find(({ level }) => level === productGoalLevel.goalLevel);
  }, [productGoalLevel, productDetails]);

  useEffect(() => {
    if (selectedProductId) {
      fetchLastPracticedAdaptiveTopicStatuses(selectedProductId);
      fetchLastPracticedWritingTopicStatuses(selectedProductId);
      fetchProductGoalLevel(selectedProductId);
    }
  }, [selectedProductId]);

  // Fetch modules with intake test scores only if that feature is available for the user
  useEffect(() => {
    if (
      selectedProductId &&
      canSetInitialProgressFromIntakeTestResults &&
      userDetails?.canInitProgressFromIntakeTest
    ) {
      fetchModulesWithIntakeTestScores(selectedProductId);
    }
  }, [selectedProductId, canSetInitialProgressFromIntakeTestResults, userDetails]);

  const getLastPracticedTopicForModule = useCallback(
    (
      module: ProductModules,
    ):
      | ResultsAPI.LastPracticedWritingTopicStatus
      | ProgressAPI.LastPracticedAdaptiveTopicStatus
      | null => {
      switch (module.type) {
        case ModuleType.ADAPTIVE:
          return (
            lastPracticedAdaptiveTopicStatuses?.find(({ moduleId }) => moduleId === module.id) ||
            null
          );
        case ModuleType.WRITING:
          return (
            lastPracticedWritingTopicStatuses?.find(({ moduleId }) => moduleId === module.id) ||
            null
          );
        default:
          return null;
      }
    },
    [lastPracticedAdaptiveTopicStatuses, lastPracticedWritingTopicStatuses],
  );

  const handleClick = () => {
    trackPlatformEvent({
      module: STUDENT_MODULES.DASHBOARD,
      itemId: PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_GO_TEST_ENVIRONMENT,
      elementId: "test-env-button-dashboard",
    });
    redirectToUrl(TEST_ENVIRONMENT_ROUTES.LANDING_PAGE);
  };

  const goToModuleWithIntakeTestScores = () => {
    const [moduleWithIntakeTestScores] = modulesWithIntakeTestScores || [];
    if (selectedProductId && moduleWithIntakeTestScores) {
      redirectToUrl(
        buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS, {
          productId: selectedProductId,
          moduleId: moduleWithIntakeTestScores.moduleId,
        }),
      );
    }
  };

  return (
    <StyledMainSection flexDirection="column" rowGap={2}>
      <section>
        <Heading1 marginBottom="2.5rem">
          {t("studentDashboard.welcomeMessage", { userName: userDetails?.firstName })}
        </Heading1>
        <ParagraphLarge data-cy="random-fact">{randomFact}</ParagraphLarge>
      </section>
      {/* -- Module cards section -- */}
      {selectedProductId && productDetails?.id === selectedProductId && (
        <section>
          <Heading3 marginBottom="2.5rem">{t("studentDashboard.continueSection.title")}</Heading3>
          <CssFlex flexDirection="row" flexWrap="wrap" gap={2}>
            {productDetails?.modules.map((module) => (
              <ModuleCard
                key={module.id}
                currentGoalLevel={currentGoalLevel}
                lastPracticedTopicStatus={getLastPracticedTopicForModule(module)}
                moduleDetails={module}
                productId={selectedProductId}
              />
            ))}
            {/* -- Coming soon modules -- */}
            {showComingSoonModules &&
              HstProductsWithPlaceholderModules.includes(productDetails.id) &&
              getComingSoonModules(t).map((module) => (
                <ComingSoonModuleCard
                  key={module.type}
                  comingSoonModule={module}
                  productId={selectedProductId}
                />
              ))}
          </CssFlex>
        </section>
      )}
      {/* -- Shortcut to test environment (for regular products only) -- */}
      {productDetails?.type === ProductType.REGULAR && (
        <>
          <Heading3>{t("studentDashboard.goToTest.title")}</Heading3>
          <StyledTestEnvCard>
            <Heading4 fontSize="1.2rem">
              {t("studentDashboard.goToTest.cardTitle", { name: userDetails?.firstName })}
            </Heading4>
            <img alt={t("studentDashboard.goToTest.imageAlt")} src={goToTestImg} />
            <StyledGoToTestButton
              dataCy="dashboard-test-env-button"
              variant="primary"
              onClick={handleClick}
            >
              {t("studentDashboard.goToTest.button")}
            </StyledGoToTestButton>
          </StyledTestEnvCard>
        </>
      )}
      {/* -- Show intake test notification if necessary -- */}
      {shouldDisplayIntakeTestNotification && (
        <Notification dataCy="intake-test-score-available-notice" position="bottomRight">
          {t("studentDashboard.intakeTestAvailableNotice.description")}
          <CssButton
            data-cy="go-to-module-with-intake-test-scores-button"
            onClick={goToModuleWithIntakeTestScores}
            variant="primary"
          >
            {t("studentDashboard.intakeTestAvailableNotice.goToModuleButton")}
          </CssButton>
        </Notification>
      )}
    </StyledMainSection>
  );
});
